import { LikeOrDislikeResponse } from "../../types/Like"

/**
 * Creates or updates a like/dislike for a firm from a specific user
 * 
 * @param {Object} params - The parameters for the like/dislike operation
 * @param {number} params.firmId - The unique identifier of the firm being liked/disliked
 * @param {string} params.frontUuid - The unique identifier of the user performing the action
 * @param {string} params.visitorId - The visitor ID of the user performing the action
 * 
 * @returns {Promise<LikeOrDislikeResponse>} A promise that resolves to an object containing:
 *  - The total count of likes for the firm
 *  - An array of like objects with details about each like
 * 
 * @example
 * // Like a firm
 * const result = await likeOrDislike({
 *   firmId: 123,
 *   frontUuid: "user-abc-123",
 *   visitorId: "visitor-xyz-789"
 * });
 * console.log(result.count); // Total number of likes
 * console.log(result.likes); // Array of like objects
 * 
 * @throws {Error} If the API request fails or returns an invalid response
 */
export async function likeOrDislike({
  firmId,
  frontUuid,
  fingerprint,
}: {
  firmId: number
  frontUuid: string
  fingerprint: string
}) {
  const response = await fetch(`https://cms.tech.lamacompta.co/api/accounting-like-custom/create`, {
    body: JSON.stringify({
      firmId,
      frontUuid,
      fingerprint,
    }),
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  })

  const responseData = (await response.json()) as LikeOrDislikeResponse
  return responseData || { count: 0, likes: [] }
}
